// The arithmetic behind a Nearmap site plan, pure. Nearmap's tiles are Web
// Mercator, 256 px, numbered like Google's (developer.nearmap.com/docs/tile-api):
// at zoom z the world is 256 · 2^z pixels across. A plan records the world
// pixel of its top-left corner at its zoom (hig_siteplan.mercator), so a GPS
// position converts straight to a point on it — the pin is placed for the
// measurer, who only drags one that's off (docs/CONDITION-REGION.md).
import { fingerprint } from '@higgins/condition-library';

const TILE_SIZE = 256;

const worldSize = (zoom: number) => TILE_SIZE * 2 ** zoom;

/** A longitude/latitude as world pixels at `zoom`. */
export function toWorld(latitude: number, longitude: number, zoom: number): { x: number; y: number } {
  const size = worldSize(zoom);
  const sin = Math.sin((latitude * Math.PI) / 180);
  return {
    x: ((longitude + 180) / 360) * size,
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * size,
  };
}

export interface PlanFrame {
  zoom: number;
  /** World pixels of the plan's top-left corner. */
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Where a GPS position falls on a plan, 0–1 across and down; null when off the plan. */
export function pinFor(latitude: number, longitude: number, frame: PlanFrame): { x: number; y: number } | null {
  const world = toWorld(latitude, longitude, frame.zoom);
  const x = (world.x - frame.left) / frame.width;
  const y = (world.y - frame.top) / frame.height;
  return x >= 0 && x <= 1 && y >= 0 && y <= 1 ? { x, y } : null;
}

/** A tile's key, as the framer keeps the tiles it has shown. */
export const tileKey = (t: { z: number; x: number; y: number }): string => `${t.z}/${t.x}/${t.y}`;

/** The tiles that cover a frame, each with where it sits in the frame (px). */
export function tilesFor(frame: PlanFrame): { z: number; x: number; y: number; dx: number; dy: number }[] {
  const tiles = [];
  const last = 2 ** frame.zoom - 1;
  const firstX = Math.floor(frame.left / TILE_SIZE);
  const firstY = Math.floor(frame.top / TILE_SIZE);
  const lastX = Math.floor((frame.left + frame.width - 1) / TILE_SIZE);
  const lastY = Math.floor((frame.top + frame.height - 1) / TILE_SIZE);
  for (let y = Math.max(0, firstY); y <= Math.min(last, lastY); y++) {
    for (let x = Math.max(0, firstX); x <= Math.min(last, lastX); x++) {
      tiles.push({ z: frame.zoom, x, y, dx: x * TILE_SIZE - frame.left, dy: y * TILE_SIZE - frame.top });
    }
  }
  return tiles;
}

/** A frame of `width` × `height` centred on a position. */
export function frameAround(latitude: number, longitude: number, zoom: number, width: number, height: number): PlanFrame {
  const centre = toWorld(latitude, longitude, zoom);
  return { zoom, left: Math.round(centre.x - width / 2), top: Math.round(centre.y - height / 2), width, height };
}

/** The same frame one zoom level in (+1) or out (−1), about its centre. */
export function rezoom(frame: PlanFrame, step: 1 | -1): PlanFrame {
  const factor = step === 1 ? 2 : 0.5;
  const cx = (frame.left + frame.width / 2) * factor;
  const cy = (frame.top + frame.height / 2) * factor;
  return { ...frame, zoom: frame.zoom + step, left: Math.round(cx - frame.width / 2), top: Math.round(cy - frame.height / 2) };
}

const FULL_TURN = 360;

/** A bearing as a whole number of degrees, 0–359 (hig_bearing's range). */
export function wholeBearing(degrees: number): number {
  return ((Math.round(degrees) % FULL_TURN) + FULL_TURN) % FULL_TURN;
}

/** The bearing (0 = north, up the plan; clockwise) from one point on the plan to another, both in plan pixels. */
export function bearingTowards(from: { x: number; y: number }, to: { x: number; y: number }): number {
  return wholeBearing((Math.atan2(to.x - from.x, from.y - to.y) * 180) / Math.PI);
}

/** The point `distance` from `from` along a bearing, in plan pixels (north up). */
export function pointAlong(from: { x: number; y: number }, bearing: number, distance: number): { x: number; y: number } {
  const radians = (bearing * Math.PI) / 180;
  return { x: from.x + distance * Math.sin(radians), y: from.y - distance * Math.cos(radians) };
}

/** A pin's radius as a share of the plan's width, so it stays finger-sized however large the plan is drawn. */
const PIN_SHARE = 0.022;
const ARROW_LENGTH = 1.9;
const ARROW_HALF_WIDTH = 0.75;
const ARROW_BASE = 0.9;

/**
 * Where a pin and its arrow sit on the plan (plan pixels), and the sizes it's
 * drawn with: one shape for the screen (SVG) and the marked-up copy (canvas).
 */
export function pinShape(planWidth: number, centre: { x: number; y: number }, bearing: number) {
  const r = planWidth * PIN_SHARE;
  const base = pointAlong(centre, bearing, r * ARROW_BASE);
  return {
    r,
    arrow: [pointAlong(centre, bearing, r * (1 + ARROW_LENGTH)), pointAlong(base, bearing - 90, r * ARROW_HALF_WIDTH), pointAlong(base, bearing + 90, r * ARROW_HALF_WIDTH)],
    ringWidth: r * 0.16,
    arrowEdge: r * 0.12,
    /** The number's baseline, below the centre so it sits in the middle. */
    textY: centre.y + r * 0.35,
    /** Just past the arrow's tip: where a round handle to turn it goes, before its own radius. */
    reach: r * (1 + ARROW_LENGTH),
  };
}

/**
 * A short fingerprint (FNV-1a, 8 hex digits) of a plan and where its placed
 * pins are and face: the same on every device for the same pins, so any iPad
 * can tell whether the marked-up copy on SharePoint still shows them.
 */
export function pinsFingerprint(planKey: string, pins: readonly { id: string; number: number; colour: string; x: number | null; y: number | null; bearing: number | null }[]): string {
  const placed = pins.filter((p) => p.x !== null && p.y !== null).map((p) => [p.id, p.number, p.colour, p.x, p.y, p.bearing]);
  return fingerprint(JSON.stringify([planKey, placed]));
}
